import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';

const SVG_SIZE = 450;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  sliderWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#6b7280',
  },
  sliderLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
    minWidth: '120px',
  },
  slider: {
    width: '180px',
    cursor: 'pointer',
    accentColor: '#3b82f6',
  },
  sliderValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#1f2937',
    minWidth: '32px',
    textAlign: 'right',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function ChordDiagram({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [threshold, setThreshold] = useState(0);

  const handleThresholdChange = useCallback((e) => {
    setThreshold(parseInt(e.target.value, 10));
  }, []);

  useEffect(() => {
    if (!data || !data.states || data.states.length === 0 || !data.matrix) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { states, matrix, colors } = data;
    const n = states.length;

    // Apply threshold: zero out entries below the minimum similarity
    const filteredMatrix = matrix.map((row) =>
      row.map((val) => (val >= threshold ? val : 0))
    );

    const radius = SVG_SIZE / 2;
    const innerRadius = radius * 0.62;
    const outerRadius = radius * 0.66;
    const labelRadius = radius * 0.74;

    svg
      .attr('viewBox', `0 0 ${SVG_SIZE} ${SVG_SIZE}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .style('max-width', `${SVG_SIZE}px`);

    const g = svg.append('g').attr('transform', `translate(${radius}, ${radius})`);

    // Chord layout
    const chord = d3
      .chord()
      .padAngle(0.04)
      .sortSubgroups(d3.descending)
      .sortChords(d3.descending);

    const chords = chord(filteredMatrix);

    // Ribbon generator
    const ribbon = d3.ribbon().radius(innerRadius);

    // Arc generator for outer arcs
    const arc = d3
      .arc()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius);

    // Draw outer arcs (groups)
    const groupSelection = g
      .selectAll('g.group')
      .data(chords.groups)
      .enter()
      .append('g')
      .attr('class', 'group')
      .style('cursor', 'pointer');

    groupSelection
      .append('path')
      .attr('d', arc)
      .attr('fill', (d) => colors[d.index % colors.length])
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .on('mouseenter', function (event, d) {
        // Highlight chords connected to this state
        ribbonSelection
          .transition()
          .duration(150)
          .attr('fill-opacity', (r) =>
            r.source.index === d.index || r.target.index === d.index ? 0.7 : 0.05
          );

        groupSelection
          .selectAll('path')
          .transition()
          .duration(150)
          .attr('opacity', (g) => (g.index === d.index ? 1 : 0.3));

        groupSelection
          .selectAll('text')
          .transition()
          .duration(150)
          .attr('opacity', (g) => (g.index === d.index ? 1 : 0.3));

        // Find top connections for this state
        const connections = [];
        for (let i = 0; i < n; i++) {
          if (i !== d.index && filteredMatrix[d.index][i] > 0) {
            connections.push({ state: states[i], score: filteredMatrix[d.index][i] });
          }
        }
        connections.sort((a, b) => b.score - a.score);

        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          type: 'arc',
          state: states[d.index],
          connections: connections.slice(0, 5),
          totalConnections: connections.length,
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
      })
      .on('mouseleave', function () {
        ribbonSelection
          .transition()
          .duration(150)
          .attr('fill-opacity', 0.4);

        groupSelection
          .selectAll('path')
          .transition()
          .duration(150)
          .attr('opacity', 1);

        groupSelection
          .selectAll('text')
          .transition()
          .duration(150)
          .attr('opacity', 1);

        setTooltip(null);
      });

    // State labels on outer arcs
    groupSelection
      .append('text')
      .each(function (d) {
        d._midAngle = (d.startAngle + d.endAngle) / 2;
      })
      .attr('dy', '0.35em')
      .attr('transform', function (d) {
        const angle = d._midAngle * 180 / Math.PI - 90;
        return `rotate(${angle}) translate(${labelRadius}) ${d._midAngle > Math.PI ? 'rotate(180)' : ''}`;
      })
      .attr('text-anchor', function (d) {
        return d._midAngle > Math.PI ? 'end' : 'start';
      })
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .attr('pointer-events', 'none')
      .text(function (d) {
        // Only show label if arc is wide enough
        return (d.endAngle - d.startAngle) > 0.08 ? states[d.index] : '';
      });

    // Draw chords (ribbons)
    const ribbonSelection = g
      .selectAll('path.chord')
      .data(chords)
      .enter()
      .append('path')
      .attr('class', 'chord')
      .attr('d', ribbon)
      .attr('fill', (d) => colors[d.source.index % colors.length])
      .attr('fill-opacity', 0.4)
      .attr('stroke', 'none')
      .style('cursor', 'pointer')
      .on('mouseenter', function (event, d) {
        d3.select(this)
          .transition()
          .duration(150)
          .attr('fill-opacity', 0.7);

        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          type: 'chord',
          sourceState: states[d.source.index],
          targetState: states[d.target.index],
          score: matrix[d.source.index][d.target.index],
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
      })
      .on('mouseleave', function () {
        d3.select(this)
          .transition()
          .duration(150)
          .attr('fill-opacity', 0.4);

        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, threshold]);

  // Empty state
  if (!data || !data.states || data.states.length === 0 || !data.matrix) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Similarity Network</div>
        <div style={styles.subtitle}>Chord thickness shows pricing pattern similarity between states</div>
        <div style={styles.emptyState}>No similarity data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Similarity Network</div>
      <div style={styles.subtitle}>Chord thickness shows pricing pattern similarity between states</div>

      <div style={styles.controls}>
        <div style={styles.sliderWrapper}>
          <span style={styles.sliderLabel}>Min similarity:</span>
          <input
            type="range"
            min="0"
            max="50"
            step="1"
            value={threshold}
            onChange={handleThresholdChange}
            style={styles.slider}
          />
          <span style={styles.sliderValue}>{threshold}</span>
        </div>
      </div>

      <div style={styles.chartContainer}>
        <svg ref={svgRef} />
      </div>

      {tooltip && tooltip.type === 'arc' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ color: '#6b7280', marginBottom: '4px' }}>
            {tooltip.totalConnections} connection{tooltip.totalConnections !== 1 ? 's' : ''}
          </div>
          {tooltip.connections.length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '4px', marginTop: '2px' }}>
              <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '2px' }}>Top similar:</div>
              {tooltip.connections.map((c) => (
                <div key={c.state} style={{ fontSize: '11px', color: '#374151' }}>
                  {c.state}: <span style={{ fontWeight: 600, color: '#2563eb' }}>
                    {c.score}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tooltip && tooltip.type === 'chord' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.sourceState} — {tooltip.targetState}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ color: '#6b7280' }}>Similarity</span>
            <span style={{ fontWeight: 600, color: '#2563eb' }}>{tooltip.score}</span>
          </div>
        </div>
      )}
    </div>
  );
}
